import React, { useState, useEffect } from "react";
import { API_URL } from "../creds";
import languages from "../utils/languages.json";
import { toast } from 'react-toastify';
import { createTaskService } from "../services/tasksService";
import SelectInput from "../components/common/select/SelectInput"



export default function AdForm({ open, onClose }) {
    const [users, setUsers] = useState([]);
    const [assignedUser, setAssignedUser] = useState("");
    const [sourceFile, setSourceFile] = useState(null);
    const [targetFile, setTargetFile] = useState(null);
    const [sourceLang, setSourceLang] = useState("en");
    const [targetLang, setTargetLang] = useState("hi");
    const [loading, setLoading] = useState(false);

    // Track touched fields & errors
    const [errors, setErrors] = useState({
        assignedUser: "",
        sourceFile: "",
        targetFile: "",
    });

    useEffect(() => {
        if (!open) return;

        const fetchUsers = async () => {
            try {
                const res = await fetch(`${API_URL}/users`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
                });
                if (!res.ok) throw new Error();
                const data = await res.json();
                setUsers(data);
            } catch (err) {
                console.error(err);
                toast.error("Failed to load users");
            }
        };

        fetchUsers();
    }, [open]);

    if (!open) return null;

    // Validate on change + blur
    const validateField = (field, value) => {
        if (field === "assignedUser" && !value) return "Please select a user";
        if (field === "sourceFile" && !value) return "Source markdown file is required";
        if (field === "targetFile" && !value) return "Target markdown file is required";
        return "";
    };

    const handleChange = (setter, field) => (e) => {
        const value = e.target.files ? e.target.files[0] : e.target.value;
        setter(value);

        // Clear error when user starts fixing it
        setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        const newErrors = {
            assignedUser: validateField("assignedUser", assignedUser),
            sourceFile: validateField("sourceFile", sourceFile),
            targetFile: validateField("targetFile", targetFile),
        };
        setErrors(newErrors);

        return !Object.values(newErrors).some((err) => err !== "");
    };

    const submit = async () => {
        if (!validateForm()) {
            toast.warn("Please fix the errors above", { autoClose: 3000 });
            return;
        }

        const formData = new FormData();
        formData.append("assigned_user", assignedUser);
        formData.append("source_lang", sourceLang);
        formData.append("target_lang", targetLang);
        formData.append("source_markdown", sourceFile);
        formData.append("target_markdown", targetFile);

        setLoading(true);
        const toastId = toast.loading("Creating translation task...");

        try {
            // eslint-disable-next-line
            const response = await createTaskService(formData);

            toast.update(toastId, {
                render: "Task created successfully!",
                type: "success",
                isLoading: false,
                autoClose: 4000,
            });

            onClose();

        } catch (err) {
            console.error(err);

            // Handle Axios errors properly
            let message = "Something went wrong.";
            if (err.response) {
                const status = err.response.status;
                switch (status) {
                    case 400:
                        message = err.response.data?.detail || "Invalid request.";
                        break;
                    case 401:
                        message = err.response.data?.detail || "Unauthorized. Please login.";
                        break;
                    case 403:
                        message = err.response.data?.detail || "Forbidden. You do not have permission.";
                        break;
                    case 404:
                        message = err.response.data?.detail || "API endpoint not found.";
                        break;
                    case 500:
                        message = err.response.data?.detail || "Server is down. Please try again later.";
                        break;
                    default:
                        message = err.response.data?.message || `Error ${status}`;
                }
            } else if (err.request) {
                message = "No response from server. Please check your network.";
            } else {
                message = err.message;
            }

            toast.update(toastId, {
                render: message,
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const languageOptions = [...languages]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((lang) => ({
            value: lang.code,
            label: `${lang.name} (${lang.code})`,
        }));

        const selectedSourceLang =
        languageOptions.find((option) => option.value === sourceLang) || null;
      const selectedTargetLang =
        languageOptions.find((option) => option.value === targetLang) || null;



    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white z-10 border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">Create New Translation Task</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="px-6 py-6 space-y-6">
                    {/* Assign User */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assign to User <span className="text-red-600">*</span>
                        </label>

                        <SelectInput
                            options={users.map((user) => ({
                                value: user.email,
                                label: `${user.name} (${user.email})`,
                            }))}
                            value={
                                assignedUser
                                    ? users
                                        .map((user) => ({
                                            value: user.email,
                                            label: `${user.name} (${user.email})`,
                                        }))
                                        .find((option) => option.value === assignedUser)
                                    : null
                            }
                            onChange={(selected) =>
                                setAssignedUser(selected ? selected.value : "")
                            }
                            placeholder="— Select user —"
                            isClearable
                            className={errors.assignedUser ? "react-select-error" : ""}
                        />
                        {errors.assignedUser && (
                            <p className="mt-1 text-sm text-red-600">{errors.assignedUser}</p>
                        )}
                    </div>

                    {/* Files */}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Source Markdown <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="file"
                            accept=".md,.markdown, .txt"
                            onChange={handleChange(setSourceFile, "sourceFile")}
                            className={`w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer ${errors.sourceFile ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.sourceFile && (
                            <p className="mt-1 text-sm text-red-600">{errors.sourceFile}</p>
                        )}
                        {sourceFile && (
                            <p className="mt-1 text-xs text-gray-500">Selected: {sourceFile.name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Target Markdown <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="file"
                            accept=".md,.markdown,.txt"
                            onChange={handleChange(setTargetFile, "targetFile")}
                            className={`w-full px-3 py-2 border rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer ${errors.targetFile ? "border-red-500" : "border-gray-300"
                                }`}
                        />
                        {errors.targetFile && (
                            <p className="mt-1 text-sm text-red-600">{errors.targetFile}</p>
                        )}
                        {targetFile && (
                            <p className="mt-1 text-xs text-gray-500">Selected: {targetFile.name}</p>
                        )}
                    </div>


                    {/* Languages (optional — no error) */}

                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source Language</label>
                        <SelectInput
                            options={languageOptions}
                            value={selectedSourceLang}
                            onChange={(selected) =>
                                setSourceLang(selected?.value || "")
                            }
                            placeholder="Select language"
                            isSearchable
                            isClearable
                            className={errors.sourceLang ? "border-red-500" : ""}
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-5">Target Language</label>
                            <SelectInput
                                options={languageOptions}
                                value={selectedTargetLang}
                                onChange={(selected) =>
                                    setTargetLang(selected?.value || "")
                                }
                                placeholder="Select language"
                                isSearchable
                                isClearable
                                className={errors.targetLang ? "border-red-500" : ""}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={loading}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 mx-auto rounded-full" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                </svg>
                                Creating...
                            </>
                        ) : (
                            "Create Task"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}